import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import { Region, type ComponentType } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { ComponentDataProvider } from '@/components/region/component-data-context';
import { TickerCarousel } from '@/extensions/ticker/components/ticker-carousel';
import { useTickerLayout } from '@/extensions/ticker/hooks/use-ticker-layout';

type TickerProps = {
    page: Promise<PageWithComponentData | null> | PageWithComponentData | null;
};

function getMainComponents(page: PageWithComponentData | null): ComponentType[] {
    const region = page?.regions?.find((entry) => entry.id === 'main');
    return (
        region?.components?.filter((component) => {
            if (!component.id || component.visible === false) {
                return false;
            }

            if (component.typeId !== 'Content.contentAsset') {
                return true;
            }

            const body =
                component.data && typeof component.data === 'object' ? (component.data as { body?: unknown }).body : '';
            return typeof body === 'string' && body.trim().length > 0;
        }) ?? []
    );
}

function TickerRuntime({ page }: { page: PageWithComponentData | null }): ReactElement | null {
    const components = getMainComponents(page);
    const isVisible = components.length > 0 && page !== null;
    useTickerLayout(isVisible);

    if (!isVisible) {
        return null;
    }

    return (
        <ComponentDataProvider value={page.componentData ?? {}}>
            <TickerCarousel components={components} />
        </ComponentDataProvider>
    );
}

function TickerDesignMode({ page }: { page: Promise<PageWithComponentData | null> | PageWithComponentData | null }) {
    useTickerLayout(true);

    return <Region page={page} regionId="main" errorElement={null} className="ticker-shell ticker-shell--design" />;
}

export function Ticker({ page }: TickerProps): ReactElement | null {
    const { isDesignMode, isPreviewMode } = usePageDesignerMode();
    const isPageDesignerActive = isDesignMode || isPreviewMode;

    if (isPageDesignerActive) {
        return <TickerDesignMode page={page} />;
    }

    if (page instanceof Promise) {
        return (
            <Suspense fallback={null}>
                <Await resolve={page} errorElement={null}>
                    {(resolvedPage) => <TickerRuntime page={resolvedPage} />}
                </Await>
            </Suspense>
        );
    }

    return <TickerRuntime page={page} />;
}
